import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { OwnerState, TwitchKanal } from '../lib/types';

/**
 * Twitch-Kanäle einer Lobby.
 *
 * Verbunden wird über Twitch selbst – wir bekommen nie ein Passwort zu sehen, nur Token, und auch
 * die liegen verschlüsselt. Hier steht deshalb nur, **welcher** Kanal verbunden ist und für
 * welches Team sein Publikum entscheidet.
 */
export function TwitchKanäle({ state }: { state: OwnerState }) {
  const [kanäle, setKanäle] = useState<TwitchKanal[] | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);

  const laden = () => {
    void api
      .twitchKanäle(state.lobby.id)
      .then(setKanäle)
      .catch(() => setKanäle([]));
  };
  useEffect(laden, [state.lobby.id]);

  // Nach der Rückkehr von Twitch steht das Ergebnis in der Adresse.
  useEffect(() => {
    const parameter = new URLSearchParams(window.location.search);
    const status = parameter.get('twitch');
    if (!status) return;
    setMeldung(
      status === 'ok'
        ? `Kanal ${parameter.get('kanal') ?? ''} verbunden.`
        : status === 'abgebrochen'
          ? 'Die Verbindung wurde abgebrochen.'
          : 'Die Verbindung hat nicht geklappt.',
    );
    laden();
  }, []);

  async function tun(was: () => Promise<unknown>, erfolg: string) {
    setLäuft(true);
    setMeldung(null);
    try {
      await was();
      setMeldung(erfolg);
      laden();
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setLäuft(false);
    }
  }

  async function verbinden() {
    setMeldung(null);
    try {
      const { url } = await api.twitchVerbinden(state.lobby.id);
      // Im selben Fenster: Twitch verweigert die Anzeige in einem eingebetteten Rahmen, und ein
      // neues Fenster wird von Browsern oft blockiert.
      window.location.assign(url);
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    }
  }

  return (
    <div className="stack">
      <ul className="zeilen">
        {(kanäle ?? []).map((kanal) => (
          <li key={kanal.id} className="zeile">
            <span className="zeile-text">{kanal.displayName}</span>
            <select
              value={kanal.teamId ?? ''}
              disabled={läuft}
              aria-label={`Team von ${kanal.displayName}`}
              onChange={(e) =>
                void tun(
                  () => api.twitchTeam(state.lobby.id, kanal.id, e.target.value || null),
                  'Zuordnung gespeichert.',
                )
              }
            >
              <option value="">ohne Team</option>
              {state.teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="ghost gefahr"
              disabled={läuft}
              onClick={() =>
                void tun(() => api.twitchTrennen(state.lobby.id, kanal.id), 'Getrennt.')
              }
            >
              Trennen
            </button>
          </li>
        ))}
        {kanäle && kanäle.length === 0 && (
          <li className="muted small">Noch kein Kanal verbunden.</li>
        )}
      </ul>

      <button type="button" onClick={() => void verbinden()}>
        Twitch-Kanal verbinden
      </button>

      <p className="muted small">
        Wir bekommen nur das Recht, im Chat mitzulesen und zu schreiben – nichts an Abonnements,
        Einnahmen oder Kanaleinstellungen. Die Zugangsdaten liegen verschlüsselt und lassen sich
        jederzeit mit „Trennen“ wieder entfernen.
      </p>

      {state.lobby.pollMode === 'PER_CHANNEL' && (
        <p className="muted small">
          Die Zuordnung entscheidet, wessen Publikum welche Challenge bestimmt. Ein Kanal ohne Team
          zählt in diesem Modus nicht mit.
        </p>
      )}

      {meldung && <p className="muted small">{meldung}</p>}
    </div>
  );
}
